import React, { useEffect, useRef, useState } from 'react'

import { Stack, Typography } from '@mui/material'

import { Alert, Button, Modal } from '@repo/ui'
import { SelectField } from 'components'
import {
  useSetConsignmentDestination,
  type ConsignmentDestination,
} from 'hooks/api'
import { CONSIGNMENT_DESTINATIONS } from 'constants/statuses'

interface SetConsignmentDestinationModalProps {
  /** The package being set. Any shape carrying `id`, `name`, `consignment_destination`. */
  packageObj?: {
    id?: string
    name?: string
    consignment_destination?: ConsignmentDestination
  } | null
  open: boolean
  onClose: () => void
  refetch?: () => void
  /**
   * False when the user lacks `certificate_create_from_package`. The trigger
   * that opens this is already disabled in that case, so this is the defensive
   * half: a modal that states a consequence and offers no way to act on it is
   * worse than one whose confirm says which permission is missing (§46).
   */
  canSet?: boolean
}

const OPTIONS = (
  Object.keys(CONSIGNMENT_DESTINATIONS) as ConsignmentDestination[]
).map(value => ({
  value,
  label: CONSIGNMENT_DESTINATIONS[value].label,
}))

/**
 * VRG-455 — record where a box of tags physically went.
 *
 * ## Why this exists at all
 *
 * A sale records a (buyer, product) pair and never names a certificate — that
 * is the premise of the whole feature — so no sale event can mark a specific
 * unit as released. The only thing that CAN narrow what a buyer's scan may
 * claim is where the stock went, which is known per BOX by whoever shipped it.
 * Without this control every package reads "not recorded" and a buyer holding a
 * valid entitlement can walk into a shop, scan a unit on the shelf, and take
 * its certificate.
 *
 * ## Why the consequence is always on screen
 *
 * Each option's real effect is shown the moment it is selected, before the
 * confirm. The choice is a single discrete pick with a downstream effect, which
 * is the case a Save button handles badly (§43): a Save gates the change
 * without ever saying what it costs. Here the cost is exactly the thing the
 * operator needs to weigh, so it is stated in the operator's terms rather than
 * left to the enum's name.
 *
 * The consequence updates as the pick changes and NOTHING is written until the
 * confirm — gate before applying, never after.
 */
export const SetConsignmentDestinationModal = (
  props: SetConsignmentDestinationModalProps,
) => {
  const { packageObj, open, onClose, refetch, canSet = true } = props

  const setDestination = useSetConsignmentDestination()

  /**
   * The package this modal is SHOWING, which is not always the one it is handed.
   *
   * The shared `Modal` fades out over `closeAfterTransition`, so these children
   * stay mounted while both call sites drop their selected package on `onClose`.
   * Holding the last one makes the closing frames identical to the open ones —
   * the same trap `AssignPackageProductModal` documents. WRITES use the live
   * `packageObj`.
   */
  const lastPackageRef = useRef<SetConsignmentDestinationModalProps['packageObj']>(null)
  if (packageObj) lastPackageRef.current = packageObj
  const shownPackage = packageObj ?? lastPackageRef.current

  const current: ConsignmentDestination =
    shownPackage?.consignment_destination ?? 'unknown'

  const [value, setValue] = useState<ConsignmentDestination>(current)
  const [error, setError] = useState<string | null>(null)

  // Seeded on OPEN, and re-seeded when the package changes, so reopening for a
  // different box never shows the previous one's pick.
  useEffect(() => {
    if (!open) return
    setValue(current)
    setError(null)
    // The mutation outlives this component on a list page that keeps it mounted
    // across every row; without a reset, one package's failure is still the
    // mutation's error state when the modal reopens for another.
    setDestination.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shownPackage?.id, current])

  const handleSave = () => {
    if (!packageObj?.id) return
    setError(null)

    setDestination.mutate(
      { id: packageObj.id, destination: value },
      {
        onSuccess: () => {
          refetch?.()
          onClose()
        },
        // Inline, not a toast: this is the answer to the confirm the user just
        // pressed, and it has to stay on screen next to the control it refused.
        onError: (mutationError: Error) => {
          setError(
            mutationError?.message ||
              'Could not record where this package went',
          )
        },
      },
    )
  }

  return (
    <Modal
      title="Where did this package go?"
      open={open}
      onClose={onClose}
      actions={
        <>
          {/* Cancel stays enabled. On an untouched modal it means "leave", and
              disabling it strands the user with no way out (§54). */}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            // Dead until the pick actually differs from what is stored, so the
            // button never promises an action that would do nothing (§54).
            disabled={
              !canSet || setDestination.isPending || value === current
            }
            // The shared Button's own mechanism (§46): a disabled button that
            // explains itself rather than swallowing the click. Both reasons
            // are real and distinct, so the one that actually applies is named.
            disabledReason={
              !canSet
                ? "You need the 'Create from package' permission. Ask an admin to grant it."
                : value === current
                  ? 'Pick a different destination to save.'
                  : undefined
            }
            loading={setDestination.isPending}
          >
            Set destination
          </Button>
        </>
      }
    >
      <Stack spacing={6}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {`This decides whether a buyer's scan can claim the certificates for the tags in ${shownPackage?.name ?? 'this package'}. Record it for stock you still hold, so a buyer cannot claim an item that has not been sold to them.`}
        </Typography>

        {error && (
          <Alert severity="error" description={error} sx={{ padding: '12px 24px' }} />
        )}

        <SelectField
          label="Destination"
          options={OPTIONS}
          value={value}
          onChange={next => {
            setError(null)
            setValue(next as ConsignmentDestination)
          }}
          disabled={!canSet || setDestination.isPending}
        />

        {/* The selected option's real effect, updated live and shown BEFORE the
            confirm. Warning severity for every option, including the ones that
            sound benign: leaving a box claimable when it is sitting on a shelf
            is precisely the harm this control prevents, and harm accrues while
            the setting is wrong in a way the setting flipping back does not
            undo. */}
        <Alert
          severity="warning"
          description={CONSIGNMENT_DESTINATIONS[value].description}
          sx={{ padding: '12px 24px' }}
        />
      </Stack>
    </Modal>
  )
}
